import React from "react";
import {useNavigate} from "react-router-dom";
import RichTextRenderer from "./RichText/RichTextRenderer";

interface Session {
  id: string;
  name: string;
  date: string;
  time: string;
  location: string;
  status: string;
  sessionData?: any;
  isSignedUp?: boolean;
}

interface TitanCardProps {
  id: string;
  name: string;
  description: string;
  dmName: string;
  upcomingSessions: Session[];
  isOpen?: boolean;
  onToggleOpen?: () => void;
}

const TitanCard: React.FC<TitanCardProps> = ({
  name,
  description,
  dmName,
  upcomingSessions,
  isOpen = false,
  onToggleOpen,
}) => {
  const navigate = useNavigate();

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't trigger card click if clicking on buttons or links
    if (
      (e.target as HTMLElement).closest("button") ||
      (e.target as HTMLElement).closest("a")
    ) {
      return;
    }
    onToggleOpen?.();
  };

  const handleButtonClick = (e: React.MouseEvent, callback?: () => void) => {
    e.stopPropagation();
    callback?.();
  };

  const handleSessionClick = (session: Session) => {
    // Navigate to sessions page with the session ID as a query parameter
    navigate(`/sessions?sessionId=${session.id}&openSignup=true`);
  };

  return (
    <div
      className={`env-character-item ${onToggleOpen ? "env-clickable-card" : ""}`}
      onClick={handleCardClick}
    >
      <div className="env-character-info">
        {/* Header with title and arrow */}
        <div className="env-card-header">
          <h3 className="env-card-title">{name}</h3>
          {onToggleOpen && (
            <button
              className="btn btn-secondary env-card-toggle"
              onClick={(e) => handleButtonClick(e, onToggleOpen)}
              aria-label={isOpen ? "Collapse" : "Expand"}
            >
              {isOpen ? "▾" : "▸"}
            </button>
          )}
        </div>

        {/* Titan details with improved spacing */}
        <div className="env-card-details">
          <p className="env-character-details">
            <strong>Dungeon Master:</strong> {dmName}
          </p>
          <p className="env-character-tokens">
            {upcomingSessions.length} upcoming session
            {upcomingSessions.length !== 1 ? "s" : ""}
          </p>
        </div>

        {/* Expanded content */}
        {isOpen && (
          <div className="env-card-expanded">
            <div className="env-card-section">
              <div className="env-card-section-title">World Description</div>
              <div className="env-card-section-content">
                <RichTextRenderer
                  content={description || "No description provided"}
                />
              </div>
            </div>
            {upcomingSessions.length > 0 && (
              <div className="env-card-section">
                <div className="env-card-section-title">Upcoming Sessions</div>
                <div className="env-card-sessions-grid">
                  {upcomingSessions.map((session, i) => (
                    <div key={i} className="env-card-session-item">
                      {session.isSignedUp ? (
                        <span>
                          {session.name} - {session.date} at {session.time} (
                          {session.location})
                          <span className="env-session-signed-up">
                            - signed up
                          </span>
                        </span>
                      ) : (
                        <button
                          onClick={(e) =>
                            handleButtonClick(e, () =>
                              handleSessionClick(session)
                            )
                          }
                          className="btn btn-tertiary env-session-link"
                        >
                          {session.name} - {session.date} at {session.time} (
                          {session.location})
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TitanCard;
